import { Banknote, Coins, CreditCard, TrendingUp, Wallet } from 'lucide-react';

import ChartCard from '@/features/reports/components/ChartCard';
import DonutChart from '@/features/reports/components/charts/DonutChart';
import { SimpleKpi } from '@/features/reports/components/KpiTile';
import ReportTable from '@/features/reports/components/ReportTable';
import { useReportOutlet } from '@/features/reports/hooks/useReportOutlet';
import { useFinanceOverview } from '@/features/reports/hooks/useReports';
import { transactionStatusLabel } from '@/features/reports/lib/filters';
import { getPersianLabel } from '@/lib/persianUi';
import {
  formatDecimal,
  formatNumber,
  formatPercent,
  formatRangeCaption,
  formatToman,
} from '@/features/reports/lib/format';

/** Platform-wide money flow: revenue split, collections, gateway fees. */
export default function FinanceReport() {
  const { query, filters } = useReportOutlet();
  const finance = useFinanceOverview(query);

  const data = finance.data;
  const methods = (data?.payment_methods ?? []).map((row) => ({
    ...row,
    label: getPersianLabel(
      { debit: 'پرداخت آنلاین', kalabarg: 'کالابرگ' },
      row.method,
      'reports.paymentMethod',
    ),
  }));
  const statuses = data?.transaction_statuses ?? [];

  return (
    <div className="reports-page">
      <div className="report-section-head">
        <h2 className="section-title">گزارش مالی</h2>
        <p className="report-section-caption">
          {formatRangeCaption(filters.from, filters.to)} • دید پلتفرمی
        </p>
      </div>

      <div className="kpi-grid report-kpi-grid">
        <SimpleKpi
          title="درآمد کالا"
          value={data?.product_revenue}
          format={formatToman}
          icon={TrendingUp}
          accent="green"
        />
        <SimpleKpi
          title="کمیسیون پلتفرم"
          value={data?.platform_commission}
          format={formatToman}
          icon={Wallet}
          accent="blue"
        />
        <SimpleKpi
          title="سهم فروشندگان"
          value={data?.vendor_share}
          format={formatToman}
          icon={Banknote}
          accent="gray"
        />
        <SimpleKpi
          title="درآمد ارسال"
          value={data?.shipping_revenue}
          format={formatToman}
          icon={Coins}
          accent="amber"
        />
        <SimpleKpi
          title="وصول‌شده از درگاه"
          value={data?.gateway_collected}
          format={formatToman}
          icon={CreditCard}
          accent="purple"
          secondary={`کالابرگ: ${formatToman(data?.kalabarg_collected ?? 0)}`}
        />
        <SimpleKpi
          title="جمع وصول‌شده"
          value={data?.total_collected}
          format={formatToman}
          icon={Coins}
          accent="green"
        />
        <SimpleKpi
          title="کارمزد درگاه"
          value={data?.gateway_fees}
          format={formatToman}
          icon={CreditCard}
          accent="red"
        />
        <SimpleKpi
          title="درآمد خالص پلتفرم"
          value={data?.net_platform_revenue}
          format={formatToman}
          icon={TrendingUp}
          accent="green"
          hint="کمیسیون پلتفرم منهای کارمزد درگاه"
        />
      </div>

      <div className="analytics-grid">
        <ChartCard
          title="سهم روش‌های پرداخت"
          subtitle="بر پایه مبلغ وصول‌شده"
          height={320}
          isLoading={finance.isLoading}
          isError={finance.isError}
          error={finance.error}
          isEmpty={!methods.length}
        >
          <DonutChart rows={methods} valueKey="amount" height={320} centerLabel="جمع وصول‌شده" />
        </ChartCard>

        <ChartCard
          title="وضعیت تراکنش‌ها"
          subtitle="مبلغ و تعداد تراکنش‌ها به تفکیک وضعیت"
          height={320}
          isLoading={finance.isLoading}
          isError={finance.isError}
          error={finance.error}
          isEmpty={!statuses.length}
        >
          <ReportTable
            rows={statuses}
            rowKey={(row) => row.status}
            columns={[
              { key: 'label', header: 'وضعیت', render: (row) => transactionStatusLabel(row.status) },
              { key: 'count', header: 'تعداد', render: (row) => formatNumber(row.count) },
              { key: 'amount', header: 'مبلغ', render: (row) => formatToman(row.amount) },
              {
                key: 'share',
                header: 'سهم',
                render: (row) => {
                  const total = statuses.reduce((sum, item) => sum + Number(item.amount ?? 0), 0);
                  return formatPercent(total ? (Number(row.amount ?? 0) / total) * 100 : 0);
                },
              },
            ]}
          />
        </ChartCard>
      </div>

      <ChartCard
        title="جزئیات روش‌های پرداخت"
        subtitle="مبلغ، تعداد تراکنش و سهم هر روش"
        bodyClassName="report-card-body-table"
        isLoading={finance.isLoading}
        isError={finance.isError}
        error={finance.error}
        isEmpty={!methods.length}
      >
        <ReportTable
          rows={methods}
          rowKey={(row) => row.method}
          columns={[
            { key: 'label', header: 'روش پرداخت' },
            { key: 'amount', header: 'مبلغ', render: (row) => formatToman(row.amount) },
            { key: 'transactions', header: 'تراکنش', render: (row) => formatNumber(row.transactions) },
            { key: 'share_pct', header: 'سهم', render: (row) => formatDecimal(row.share_pct, 1) + '٪' },
            {
              key: 'avg',
              header: 'میانگین هر تراکنش',
              render: (row) =>
                formatToman(row.transactions ? Number(row.amount ?? 0) / row.transactions : 0),
            },
          ]}
        />
      </ChartCard>
    </div>
  );
}
